import { useEffect, useState } from 'react';
import { useProductCatalogRepo } from '@hotjar/product-catalog';
import type { Currency } from '../api/common/model/customer';
import type { BillingPeriod, ProductAllowance } from '../api/common/model/subscription';
import type { Site } from '../draft-order';

type IsCancellingProps = {
  productAllowances: ProductAllowance[];
  sites: Site[] | undefined;
  isBillingV3: boolean;
  billingPeriod: BillingPeriod;
  currency: Currency;
};

/**
 * This hook checks if the user intends to cancel a subscription (= downgrade all the plans
 * of all sites to basic).
 * The productAllowances needs to be the complete list of allowances for all the sites
 * (both the existing ones and the new ones).
 */
export function useIsCancellingSubscription({
  productAllowances,
  sites,
  isBillingV3,
  billingPeriod,
  currency,
}: IsCancellingProps): boolean {
  const [isCancelling, setIsCancelling] = useState(false);
  const repo = useProductCatalogRepo({ enabled: isBillingV3, billingPeriod, currency });

  useEffect(() => {
    if (sites && isBillingV3) {
      const basicAllowances = repo.getBasicAllowanceSkus();

      setIsCancelling(productAllowances.every((product) => basicAllowances?.includes(product.sku)));
    }
  }, [sites, isBillingV3, productAllowances, repo]);

  return isCancelling;
}
